import { Store } from 'libx'
import { observable, action, computed, runInAction, toJS } from 'mobx'
import { browserHistory } from 'react-router'
import { task } from 'mobx-task'
import { debouncePromise } from 'utils/promiseUtil'
import analytics from 'misc/analytics'

// Step states
// import LinkAccountsState from 'screens/ProjectWizard/Steps/LinkAccounts/LinkAccountsState'
import ProjectTypeState from 'screens/ProjectWizard/Steps/ProjectType/ProjectTypeState'
import TaxYearsState from 'screens/ProjectWizard/Steps/TaxYears/TaxYearsState'
import TaxStatesState from 'screens/ProjectWizard/Steps/TaxStates/TaxStatesState'
import QuestionsState from 'screens/ProjectWizard/Steps/Questions/QuestionsState'
// import UploadDocumentsState from 'screens/ProjectWizard/Steps/UploadDocuments/UploadDocumentsState'
import FeeBreakdownState from 'screens/ProjectWizard/Steps/FeeBreakdown/FeeBreakdownState'
import WizardCompletedState from 'screens/ProjectWizard/Steps/WizardCompleted/WizardCompletedState'
import links from '../../misc/links'

export default class ProjectWizardStore extends Store {
  @observable currentStep = null
  @observable steps = []
  @observable isProcessing = false
  @observable hereForFreeExtensions = false
  @observable job
  @observable stepDelta = 0 // this is for animation purposes

  constructor () {
    super(...arguments)
    this._doSyncJob = debouncePromise(this._doSyncJob)
    this.rootStore.authStore.onLogin(() => this.resetSteps())
    this.rootStore.authStore.onLogout(() => this.resetSteps())
  }

  @computed get showBackButton () {
    // Only show back button after the first step,
    // and not on the last step (which is after payment)
    return !this.currentStep.hideBackButton
  }

  /**
   * Used by steps to derive data from other steps.
   */
  getStep (stepId) {
    const step = this.steps.find(x => x.stepId === stepId)
    if (!step) {
      throw new Error(`Step "${stepId}" not found.`)
    }

    return step
  }

  /**
   * Called by the view when activated.
   */
  @task async activate (id) {
    this.rootStore.rateProjectStore.ensureAllRated()
    if (id) {
      if (this.job && this.job.id === id) {
        return
      }

      const job = await this.rootStore.projectStore.fetchProject(id)
      if (job.status !== 'UNDER_CONSTRUCTION') {
        browserHistory.replace(links.projectDetails(job.id))
        return
      }
      this.setJob(job)
      await this.hydrateJob()
      this._restoreStep()
      return
    } else {
      this.setJob(null)
    }

    this.resetSteps()
  }

  /**
   * Sets the current job being edited.
   */
  @action setJob (job) {
    this.job = job
  }

  /**
   * Trashes current state.
   */
  @action resetSteps () {
    this.steps.forEach(step => {
      step.deactivate && step.deactivate()
      step.destroy && step.destroy()
    })
    const steps = this._createSteps([
      ProjectTypeState,
      TaxYearsState,
      QuestionsState,
      TaxStatesState,
      // LinkAccountsState,
      // UploadDocumentsState,
      FeeBreakdownState,
      WizardCompletedState
    ])
    this.steps.replace(steps)
    this.steps.forEach(step => step.initialize && step.initialize())
    this.setStep(this.steps[0])
  }

  @action setStep (step) {
    if (!step) {
      return
    }
    const idxBefore = this.steps.indexOf(this.currentStep)
    const idxAfter = this.steps.indexOf(step)
    if (step.relevant === false) {
      const direction = (idxBefore - idxAfter) < 0
        ? 1
        : -1
      return this.setStep(this.steps[idxAfter + direction])
    }

    // Give the step a hook to clean up
    if (this.currentStep && this.currentStep.deactivate) {
      this.currentStep.deactivate()
    }
    this.currentStep = step
    this.stepDelta = (idxAfter - idxBefore) < 0 ? -1 : 1
    // Give the step a hook to load data when relevant.
    if (this.currentStep.activate) {
      this.currentStep.activate()
    }
    this.updateIntercom({
      wizard_step: this.currentStep.stepId
    })
    this.trackStep()
  }

  @action setProcessing (processing) {
    this.isProcessing = processing
  }

  @action back () {
    const idx = this.steps.indexOf(this.currentStep)
    if (idx === 0) return
    const prevStep = this.steps[idx - 1]
    const back = () => this.setStep(prevStep)
    if (this.currentStep.back) {
      // Step can hijack the back function
      // Inject the actual back call so it
      // can call it when ready.
      this.currentStep.back(back)
      return
    }

    back()
  }

  @action async next () {
    if (this.currentStep.next) {
      this.setProcessing(true)
      // This step wants to handle "next"
      const canContinue = await this.currentStep.next()
      if (!canContinue) {
        this.setProcessing(false)
        return
      }
    }

    this.setProcessing(true)
    try {
      await this.syncJob()
    } catch (err) {
      runInAction(() => {
        this.rootStore.flashMessageStore.create(err.message).failed().autoDismiss()
      })
      throw err
    } finally {
      this.setProcessing(false)
    }

    const idx = this.steps.indexOf(this.currentStep)
    if (idx === this.steps.length - 1) {
      return
    }

    const nextStep = this.steps[idx + 1]
    this.setStep(nextStep)
  }

  async syncJob () {
    if (this.currentStep === this.getStep('ProjectType') || this.currentStep === this.getStep('FeeBreakdown')) {
      return
    }

    // This is debounced.
    return this._doSyncJob()
  }

  async _doSyncJob () {
    const data = this.steps.map(x => x.serializeToJob && x.serializeToJob()).reduce((x, y) => ({ ...x, ...toJS(y) }), {})
    if (!this.job) {
      const newJob = await this.rootStore.projectStore.createJob(data)
      this.setJob(newJob)
      browserHistory.replace(links.projectEditor(this.job.id))
    } else {
      await this.rootStore.projectStore.updateJob(this.job.id, data)
    }
  }

  async hydrateJob () {
    this.resetSteps()
    await Promise.all(this.steps.map(action(x => x.hydrate && x.hydrate(this.job))))
  }

  @action _restoreStep () {
    if (this.currentStep === this.getStep('ProjectType')) {
      // Check to see if we can guess what step to put them on.
      let step = this.getStep('TaxYears')
      if (this.job.years.length > 0 || step.relevant === false) {
        step = this.getStep('Questions')
        if (this.job.hasAnsweredAllQuestions) {
          step = this.getStep('TaxStates')
          if (this.job.states.length > 0 || step.relevant === false) {
            step = this.getStep('FeeBreakdown')
          }
        }
      }

      this.setStep(step)
    }
  }

  /**
   * Shortcut to instantiate the steps, passing in this wizard instance.
   */
  _createSteps (stepStates) {
    return stepStates.map(StepState => new StepState({ wizard: this }))
  }

  /**
   * Updates Intercom attributes.
   */
  updateIntercom (attrs) {
    this.rootStore.intercomStore.update(attrs)
  }

  /**
   * Sends data to Segment.
   */
  trackStep () {
    if (this.currentStep.trackingId) {
      analytics.page(this.currentStep.trackingId)
    }
  }
}



// WEBPACK FOOTER //
// ./src/client/store/ui/ProjectWizardStore.js